import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Checkbox, CircularProgress, IconButton, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { StorefrontAdminProduct, storefrontAdminApi } from '../../../services/storefrontService';
import MaterialSymbol from '../../common/MaterialSymbol';

/** Reihenfolge, Empfehlung und Sichtbarkeit der Produkte in der Storefront (Content-Manager). */
const StorefrontProductsPanel: React.FC = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const [products, setProducts] = useState<StorefrontAdminProduct[] | null>(null);
    const [error, setError] = useState(false);
    const tr = (key: string) => translate(`storefront.admin.products.${key}`);

    const load = useCallback(() => {
        storefrontAdminApi.products().then(setProducts).catch(() => setError(true));
    }, []);
    useEffect(load, [load]);

    if (error) return <Alert severity="error">{tr('updateError')}</Alert>;
    if (!products) return <CircularProgress />;

    const fail = () => notify(tr('updateError'), { type: 'error' });

    const patch = async (p: StorefrontAdminProduct, change: Partial<Pick<StorefrontAdminProduct, 'status' | 'isFeatured'>>) => {
        try {
            await storefrontAdminApi.patchProduct(p.id, change);
            setProducts((list) => (list ?? []).map((x) => (x.id === p.id ? { ...x, ...change } : x)));
        } catch {
            fail();
        }
    };

    const move = async (index: number, delta: -1 | 1) => {
        const next = [...products];
        const target = index + delta;
        if (target < 0 || target >= next.length) return;
        [next[index], next[target]] = [next[target], next[index]];
        try {
            await storefrontAdminApi.reorder(next.map((p) => p.id));
            setProducts(next.map((p, i) => ({ ...p, position: i })));
        } catch {
            fail();
        }
    };

    return (
        <Box>
            <Typography color="text.secondary" sx={{ mb: 2 }}>{tr('help')}</Typography>
            <Button component={RouterLink} to="/products" size="small" sx={{ mb: 2 }}>{tr('manage')}</Button>
            {products.length === 0 ? (
                <Typography>{tr('empty')}</Typography>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{tr('position')}</TableCell>
                                <TableCell>{tr('title')}</TableCell>
                                <TableCell>{tr('price')}</TableCell>
                                <TableCell>{tr('status')}</TableCell>
                                <TableCell>{tr('featured')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {products.map((p, index) => (
                                <TableRow key={p.id} data-testid={`storefront-admin-product-${p.id}`}>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        <IconButton size="small" aria-label={`${tr('up')}: ${p.title}`} disabled={index === 0} onClick={() => move(index, -1)}>
                                            <MaterialSymbol name="expand_more" style={{ transform: 'rotate(180deg)' }} />
                                        </IconButton>
                                        <IconButton size="small" aria-label={`${tr('down')}: ${p.title}`} disabled={index === products.length - 1} onClick={() => move(index, 1)}>
                                            <MaterialSymbol name="expand_more" />
                                        </IconButton>
                                    </TableCell>
                                    <TableCell>{p.title}</TableCell>
                                    <TableCell>{p.price ? `${p.price.amount} ${p.price.currency}` : '—'}</TableCell>
                                    <TableCell>
                                        <Select size="small" value={p.status} onChange={(e) => patch(p, { status: e.target.value as StorefrontAdminProduct['status'] })} inputProps={{ 'aria-label': `${tr('status')}: ${p.title}` }}>
                                            <MenuItem value="published">{tr('statusPublished')}</MenuItem>
                                            <MenuItem value="draft">{tr('statusDraft')}</MenuItem>
                                            <MenuItem value="archived">{tr('statusArchived')}</MenuItem>
                                        </Select>
                                    </TableCell>
                                    <TableCell>
                                        <Checkbox checked={p.isFeatured} onChange={(e) => patch(p, { isFeatured: e.target.checked })} inputProps={{ 'aria-label': `${tr('featured')}: ${p.title}` }} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}
        </Box>
    );
};

export default StorefrontProductsPanel;
